import React, { createContext, useContext, useState, useEffect } from "react";
import {
  verifyToken,
  login as loginService,
  googleLogin as googleLoginService,
  logout as logoutService,
  register as registerService,
} from "../services/AuthServices";
import { useNavigate } from "react-router-dom";
import { User, updateUser } from "../services/UserServices";

interface AuthContextType {
  user: User | null;
  handleUpdateUser: (
    User: Partial<
      Omit<User, "id" | "created_at" | "updated_at" | "last_connection">
    >
  ) => void;
  login: (email: string, password: string) => Promise<boolean>;
  googleLogin: (code: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  register: (
    email: string,
    password: string,
    onError?: (error: string) => void
  ) => Promise<boolean>;
  error: string | null;
}

// Create a context with default values
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create the AuthProvider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser).username : null;
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!user) {
          const verifiedUser = await verifyToken();
          if (verifiedUser) {
            setUser(verifiedUser);
            localStorage.setItem("user", JSON.stringify(verifiedUser));
          } else {
            setUser(null);
          }
        } else {
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
          }
        }
      } catch (error) {
        console.error("error during checkAuth", error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateUser = async (
    updatedUser: Partial<
      Omit<User, "id" | "created_at" | "updated_at" | "last_connection">
    >
  ) => {
    try {
      const newUser = await updateUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(newUser));
      setUser(newUser);
    } catch {
      //TODO err
    }
  };
  const login = async (email: string, password: string) => {
    const loggedInUser = await loginService(email, password);
    if (loggedInUser) {
      setUser(loggedInUser);
      localStorage.setItem("user", JSON.stringify(loggedInUser));
      return true;
    } else {
      return false;
      // throw new Error("Login failed: Invalid credentials");
    }
  };

  const googleLogin = async (code: string) => {
    const loggedInUser = await googleLoginService(code);
    if (loggedInUser) {
      setUser(loggedInUser);
      localStorage.setItem("user", JSON.stringify(loggedInUser));
      return true;
    } else {
      return false;
    }
  };

  const logout = async () => {
    await logoutService();
    setUser(null);
    localStorage.removeItem("user");
    navigate("/");
  };

  //for some reason, this makes the component re-render on error
  const register = async (
    email: string,
    password: string,
    onError?: (error: string) => void
  ): Promise<boolean> => {
    setError(null);

    try {
      const res = await registerService(email, password);

      if (res.status === "success") {
        setUser(res.user);
        localStorage.setItem("user", JSON.stringify(res.user));
        return true;
      }

      // Handle user exists and other errors in one place
      const errorMessage =
        res.status === "user_exists"
          ? "Email already used"
          : res.message || "Registration failed";

      if (onError) {
        onError(errorMessage);
      }

      setError(errorMessage);
      return false;
    } catch (err) {
      console.error("Registration error:", err);

      const errorMessage = "An unexpected error occurred";
      if (onError) {
        onError(errorMessage);
      }

      setError(errorMessage);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        handleUpdateUser,
        login,
        googleLogin,
        logout,
        loading,
        register,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to access the AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
